import React, { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ConfirmModal from '../../components/common/ConfirmModal/ConfirmModal';
import DataTable from '../../components/common/DataTable/DataTable';
import EmptyState from '../../components/common/EmptyState/EmptyState';
import Loading from '../../components/common/Loading/Loading';
import PageContainer from '../../components/common/PageContainer/PageContainer';
import PageSizeSelect from '../../components/common/PageSizeSelect/PageSizeSelect';
import Pagination from '../../components/common/Pagination/Pagination';
import { parseCommercialBooleanFilter } from '../../constants/commercialFilters';
import { getParqAnswerLabel } from '../../constants/parqQuestions';
import { useCommercialLeadStatusPolling } from '../../hooks/useCommercialLeadStatusPolling';
import { useOnboardingLeads } from '../../hooks/useOnboardingLeads';
import { usePageSize } from '../../hooks/usePageSize';
import { useQueryFilters } from '../../hooks/useQueryFilters';
import type { CheckoutLead, ExistingStudentContactMatch, OnboardingLeadFilters } from '../../types';
import { maskPhoneByCountry } from '../../utils/masks';
import OnboardingFilters from './OnboardingFilters';
import { buildOnboardingLeadRowCells, getOnboardingLeadRowKey } from './components/OnboardingLeadRow';
import { ONBOARDING_QUERY_FILTER_SCHEMA } from './onboardingQueryFilters';
import styles from './Onboarding.module.css';

function formatExistingMatchPhone(match: ExistingStudentContactMatch): string | null {
  if (!match.phoneNumber) return null;
  const callingCode = match.phoneCountryCode || '55';
  return `+${callingCode} ${maskPhoneByCountry(match.phoneNumber, callingCode)}`;
}

const ParqAlertsModalContent: React.FC<{ lead: CheckoutLead }> = ({ lead }) => (
  <div className={styles['plan-modal-content']}>
    <p className={styles['parq-modal-intro']}>
      Perguntas do PAR-Q respondidas com <strong>Sim</strong> por {lead.student_name}:
    </p>
    {lead.parq_yes_answers.answers.length === 0 && (
      <p className={styles['parq-modal-intro']}>Nenhuma pergunta com resposta &quot;Sim&quot; foi encontrada.</p>
    )}
    {lead.parq_yes_answers.answers.map((answer) => (
      <div key={answer.field} className={styles['parq-alert-item']}>
        <div className={styles['plan-detail-row']}>
          <span>Pergunta</span>
          <strong>{getParqAnswerLabel(answer)}</strong>
        </div>
        <div className={styles['plan-detail-row']}>
          <span>Resposta</span>
          <strong className={styles['parq-answer-yes']}>Sim</strong>
        </div>
        {answer.detail && (
          <div className={styles['plan-detail-row']}>
            <span>Detalhe</span>
            <strong>{answer.detail}</strong>
          </div>
        )}
      </div>
    ))}
  </div>
);

const OnboardingPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    filters: queryFilters,
    setFilter,
    setFilters,
    resetFilters,
  } = useQueryFilters(ONBOARDING_QUERY_FILTER_SCHEMA, { pageKey: 'page' });

  const {
    searchName,
    classifications: classificationFilters,
    projectCodes: projectCodeFilters,
    hasPhotos: hasPhotosFilter,
    hasParq: hasParqFilter,
    hasAnamnesis: hasAnamnesisFilter,
    joinedAtStart,
    joinedAtEnd,
  } = queryFilters;

  const [selectedParqLead, setSelectedParqLead] = useState<CheckoutLead | null>(null);
  const [existingStudentMatchModal, setExistingStudentMatchModal] = useState<ExistingStudentContactMatch | null>(null);

  const {
    page,
    pageSize,
    pageSizeOptions,
    handlePageChange,
    handlePageSizeChange,
  } = usePageSize('onboarding_page_size');

  const filters: OnboardingLeadFilters = useMemo(
    () => ({
      searchName: searchName || undefined,
      classifications: classificationFilters.length > 0 ? classificationFilters : undefined,
      projectCodes: projectCodeFilters.length > 0 ? projectCodeFilters : undefined,
      hasPhotos: parseCommercialBooleanFilter(hasPhotosFilter),
      hasParq: parseCommercialBooleanFilter(hasParqFilter),
      hasAnamnesis: parseCommercialBooleanFilter(hasAnamnesisFilter),
      joinedAtStart: joinedAtStart || undefined,
      joinedAtEnd: joinedAtEnd || undefined,
    }),
    [
      searchName,
      classificationFilters,
      projectCodeFilters,
      hasPhotosFilter,
      hasParqFilter,
      hasAnamnesisFilter,
      joinedAtStart,
      joinedAtEnd,
    ],
  );

  const hasActiveFilters = !!(
    searchName
    || classificationFilters.length > 0
    || projectCodeFilters.length > 0
    || hasPhotosFilter
    || hasParqFilter
    || hasAnamnesisFilter
    || (joinedAtStart && joinedAtEnd)
  );

  const handleClearFilters = useCallback(() => {
    resetFilters();
  }, [resetFilters]);

  const {
    leads,
    pagination,
    loading,
    error,
    refresh,
    patchLeadOnboardingStatus,
  } = useOnboardingLeads(page, pageSize, filters);

  useCommercialLeadStatusPolling(
    leads,
    filters,
    pagination.totalRecords,
    patchLeadOnboardingStatus,
    () => refresh({ silent: true }),
    'onboarding',
  );

  const openStudentDetails = useCallback((studentId: number | null) => {
    if (studentId == null) return;
    navigate(`/alunas/${studentId}/editar`);
  }, [navigate]);

  if (error) return <EmptyState message={`Erro: ${error}`} />;

  return (
    <PageContainer>
      <OnboardingFilters
        filters={queryFilters}
        loading={loading}
        totalRecords={pagination.totalRecords}
        hasActiveFilters={hasActiveFilters}
        setFilter={setFilter}
        setFilters={setFilters}
        onClearFilters={handleClearFilters}
      />

      <div className={styles['list-body']}>
        {loading && leads.length === 0 ? (
          <Loading />
        ) : leads.length === 0 ? (
          <EmptyState message={hasActiveFilters
            ? 'Nenhuma aluna encontrada com os filtros selecionados.'
            : 'Nenhuma pré-aluna ou aluna nova encontrada.'} />
        ) : (
          <DataTable
            headers={[
              'Nome da aluna',
              'Tags',
              'Data pagamento',
              <span key="link" className={styles['header-center']}>Link</span>,
              <span key="anamnesis" className={styles['header-center']}>Anamnese</span>,
              <span key="parq" className={styles['header-center']}>Par-Q</span>,
              <span key="photos" className={styles['header-center']}>Fotos</span>,
            ]}
            gridTemplate="minmax(220px, 2.2fr) minmax(148px, 1.1fr) minmax(120px, 1fr) minmax(168px, 1.15fr) 88px 88px 88px"
            highlightTerm={searchName}
            className={`${styles['onboarding-table']} ${loading ? styles['onboarding-table-loading'] : ''}`.trim()}
          >
            {leads.map((lead) => (
              <DataTable.Row
                key={getOnboardingLeadRowKey(lead)}
                sensitive={Boolean(lead.is_sensitive)}
              >
                {buildOnboardingLeadRowCells({
                  lead,
                  onOpenStudentDetails: openStudentDetails,
                  onOpenExistingStudentMatch: setExistingStudentMatchModal,
                  onOpenParqAlerts: setSelectedParqLead,
                })}
              </DataTable.Row>
            ))}
          </DataTable>
        )}
      </div>

      {leads.length > 0 && (
        <div className={styles['bottom-row']}>
          <PageSizeSelect
            value={pageSize}
            options={pageSizeOptions}
            onChange={handlePageSizeChange}
          />
          <div className={styles['pagination-center']}>
            <Pagination
              pagination={pagination}
              loading={loading}
              onPageChange={handlePageChange}
            />
          </div>
        </div>
      )}

      <ConfirmModal
        open={selectedParqLead !== null}
        title="Resposta do PAR-Q"
        variant="warning"
        icon="health_and_safety"
        confirmLabel="Fechar"
        hideCancel
        onConfirm={() => setSelectedParqLead(null)}
        onCancel={() => setSelectedParqLead(null)}
      >
        {selectedParqLead && <ParqAlertsModalContent lead={selectedParqLead} />}
      </ConfirmModal>

      <ConfirmModal
        open={existingStudentMatchModal !== null}
        title="Contato já cadastrado"
        variant="warning"
        icon="warning"
        confirmLabel="Fechar"
        hideCancel
        onConfirm={() => setExistingStudentMatchModal(null)}
        onCancel={() => setExistingStudentMatchModal(null)}
      >
        {existingStudentMatchModal && (
          <div className={styles['existing-student-match-content']}>
            <p>
              Este contato já aparece em outra aluna cadastrada:
            </p>
            <ul>
              <li>
                <strong>Nome:</strong>
                {' '}
                {existingStudentMatchModal.fullName || `#${existingStudentMatchModal.studentId}`}
              </li>
              {existingStudentMatchModal.email && (
                <li>
                  <strong>E-mail:</strong>
                  {' '}
                  {existingStudentMatchModal.email}
                </li>
              )}
              {formatExistingMatchPhone(existingStudentMatchModal) && (
                <li>
                  <strong>Telefone:</strong>
                  {' '}
                  {formatExistingMatchPhone(existingStudentMatchModal)}
                </li>
              )}
            </ul>
          </div>
        )}
      </ConfirmModal>
    </PageContainer>
  );
};

export default OnboardingPage;
